// Static genplan data bundled with the page. Heavy layers (PMTiles) are fetched
// from DATA_URL: /genplan-data in dev (served by vite.config.ts), object storage in prod.
import zonesJson from '../../../../genplan/data/zones.json';
import metaJson from '../../../../genplan/data/meta.json';

export type Lang = 'ru' | 'ky' | 'en';

export interface Zone {
  id: string;
  layer: 'city' | 'outside';
  name: { ru: string; ky: string | null; en: string | null };
  color: string;
  texture: string | null;
  floors: { min?: number; max?: number; label: string } | null;
  description: string | null;
  includes: string[] | null;
  description_ky?: string | null;
  includes_ky?: string[] | null;
  area_official_ha: { existing: number | null; plan_2050: number | null } | null;
  density: string | null;
  source: string[] | null;
}

export const ZONES = zonesJson.zones as Zone[];
export const ZONE_BY_ID: Record<string, Zone> = Object.fromEntries(ZONES.map((z) => [z.id, z]));

export const META = metaJson as unknown as {
  bounds: [number, number, number, number];
  accuracy_m: { rmse_check: number; p90_check: number; gcps: number };
  accuracy_label_m: number;
  zones: Record<string, { area_ha: number; polygons: number }>;
};

// Street network polygons (their own vector layer, not a zone).
export const ROAD_ID = 'road';
export const ROAD_COLOR = '#6B7280';

const rawDataUrl = (import.meta.env.VITE_GENPLAN_DATA_URL as string | undefined) || '/genplan-data';
export const DATA_URL = new URL(rawDataUrl.replace(/\/$/, ''), window.location.origin).href;

// Bishkek + suburbs, [W, S, E, N] — search bias and max bounds.
export const SEARCH_BOX: [number, number, number, number] = [74.35, 42.7, 74.8, 43.12];
// Chuy Region bounding box (src/data/geography/kyrgyzstan-regions.geojson, CHUI) + a small margin:
// the genplan map can't be panned or zoomed out beyond the oblast.
export const MAX_BOUNDS: [[number, number], [number, number]] = [
  [72.95, 41.8],
  [77.32, 43.32],
];
export const INITIAL_CENTER: [number, number] = [74.595, 42.87];
export const INITIAL_ZOOM = 12;

export function zoneName(z: Zone | undefined, lang: Lang): string {
  if (!z) return '';
  return z.name[lang] || z.name.ru;
}

// Short floor badge from the genplan legend/note ("до 4 эт.", "5–8 эт.", "15+ эт."); null when not stated.
export function floorsShort(z: Zone | undefined): string | null {
  const f = z?.floors;
  if (!f) return null;
  if (f.min != null && f.max != null) return `${f.min}–${f.max} эт.`;
  if (f.max != null) return `до ${f.max} эт.`;
  if (f.min != null) return `${f.min}+ эт.`;
  return null;
}

// Territories outside the city limits: hidden by default so the city zones sit
// directly on the base map instead of looking like a pasted sheet.
export const OUTSIDE_IDS = ZONES.filter((z) => z.layer === 'outside').map((z) => z.id);

// Zone texts in the UI language: official Kyrgyz note for ky, Russian note otherwise.
export function zoneText(z: Zone, lang: Lang): { description: string | null; includes: string[] | null } {
  if (lang === 'ky' && (z.description_ky || z.includes_ky)) {
    return { description: z.description_ky ?? null, includes: z.includes_ky ?? null };
  }
  return { description: z.description, includes: z.includes };
}

const SOURCE_KY: Record<string, string> = {
  'Легенда основного чертежа (Приложение 1)': 'Негизги чийменин шарттуу белгилери (1-тиркеме)',
  'Основные положения (пояснительная записка), гл. 6 «Функциональное зонирование»':
    'Негизги жоболор (түшүндүрмө кат), 6-глава «Функционалдык зоналаштыруу»',
  'Основные положения (пояснительная записка), гл. 11 «Основные технико-экономические показатели»':
    'Негизги жоболор (түшүндүрмө кат), 11-глава «Негизги техникалык-экономикалык көрсөткүчтөр»',
};

export function zoneSources(z: Zone, lang: Lang): string[] {
  return (z.source || []).map((src) => (lang === 'ky' && SOURCE_KY[src]) || src);
}
